import { useCallback, useEffect, useRef, useState } from 'react'
import {
    VOICE_SOURCES,
    isCallSource,
    isDisplayAudioSupported,
    noAudioSharedMessage,
} from '../utils/voiceSource'

// RMS of the signal above which we consider someone to be speaking.
const SPEECH_RMS_THRESHOLD = 0.015
// Two-stage cut, tuned for statements of one to three sentences (~8–15 s of
// speech) so the graph moves every few seconds even during a monologue:
//  - a pause of `pauseMs` after speech ends the statement;
//  - once `softCapMs` of speech has accumulated, a mere breath of `breathMs`
//    ends it too, so cuts land between sentences rather than mid-word;
//  - `maxSegmentMs` of speech cuts regardless (calls stay under the diarizing
//    model's 30 s chunking threshold).
// Calls flow faster than dictation, hence the shorter values.
const SEGMENTING = {
    mic: {
        pauseMs: 1500,
        softCapMs: 10 * 1000,
        breathMs: 400,
        maxSegmentMs: 20 * 1000,
    },
    call: {
        pauseMs: 1000,
        softCapMs: 8 * 1000,
        breathMs: 400,
        maxSegmentMs: 15 * 1000,
    },
}
// A silent recorder is restarted this often so a long quiet stretch never
// accumulates into one big (discarded) file.
const SILENT_RECYCLE_MS = 60 * 1000

// The live preview comes from short clips of the audio sent to the plain
// (cheap) model while the segment is still recording: a clip closes at the
// first breath after `minMs` of speech, or at `maxMs`.
const PREVIEW_CHUNK = { minMs: 1500, breathMs: 250, maxMs: 4000 }
const LEVEL_POLL_MS = 100

// Mic-only clip of the user's own voice, sent with diarized requests so
// their turns come back as "speaker: me".
const REFERENCE_CLIP_MS = 4000
const REFERENCE_MIN_SPEECH_MS = 1500
const REFERENCE_MAX_ATTEMPTS = 3

const MIME_CANDIDATES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
]

export const isVoiceDictationSupported = () =>
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined' &&
    !!(window.AudioContext || window.webkitAudioContext)

const pickMimeType = () =>
    MIME_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type)) || ''

// OpenAI infers the container from the filename extension.
const extensionFor = mimeType => {
    if (mimeType.includes('mp4')) return 'mp4'
    if (mimeType.includes('ogg')) return 'ogg'
    return 'webm'
}

const describeMicError = err => {
    if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        return 'Microphone access was denied. Allow it in your browser settings to dictate statements.'
    }
    if (err?.name === 'NotFoundError') {
        return 'No microphone was found on this device.'
    }
    return 'Could not start the microphone.'
}

const describeDisplayError = err => {
    if (/gesture|activation/i.test(err?.message || '')) {
        return 'Your browser needs a direct click to share audio: click the microphone in the statement form and choose the source again.'
    }
    if (err?.name === 'NotAllowedError') return 'Screen sharing was cancelled.'
    if (err?.name === 'NotFoundError') {
        return 'No screen or tab is available to share.'
    }
    return 'Could not start sharing the audio.'
}

const joinWords = parts => parts.filter(Boolean).join(' ')

const readRms = analyser => {
    const samples = new Float32Array(analyser.fftSize)
    analyser.getFloatTimeDomainData(samples)
    let sum = 0
    for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i]
    return Math.sqrt(sum / samples.length)
}

// `transcribe` may answer { text } (plain) or { statements, text } (diarized).
const normaliseResult = result => {
    if (typeof result === 'string')
        return { text: result.trim(), statements: [] }
    return {
        text: (result?.text || '').trim(),
        statements: Array.isArray(result?.statements) ? result.statements : [],
    }
}

const freshReference = () => ({
    blob: null,
    recorder: null,
    attempts: 0,
    speechMs: 0,
    timer: null,
})

/**
 * Continuous voice input from the microphone, a shared browser tab or the
 * screen's audio (a call or a video), optionally mixed with the microphone.
 *
 * The audio is split into segments at long pauses; each segment that
 * contained speech is run through `transcribe(blob, filename, meta)` and its
 * result handed to `onStatement({ text, statements })`, strictly in spoken
 * order (the next waits for the previous promise) so statements land in the
 * graph as they were said. `meta` carries `{ mode, source, speakerReference }`
 * — call sources use `mode: 'diarize'` and get one statement per speaker
 * turn, with the user's own turns pinned to "me" by a short mic-only
 * reference clip recorded at the start of the session.
 *
 * A live, tentative transcript streams through `onPreview(text)` while a
 * segment records: short clips of the audio (`PREVIEW_CHUNK`) go to the plain
 * model, which detects the language like the final transcription does. (The
 * browser's own SpeechRecognition was dropped: it is locked to the browser's
 * UI language and cannot hear a shared tab.) The preview covers the words not
 * yet confirmed by `transcribe`, so a finished segment's words stay visible
 * until its final text arrives.
 *
 * `onEnded(reason)` fires after listening stops on its own, e.g. when the
 * user clicks the browser's "Stop sharing" bar.
 */
export default function useVoiceDictation({
    transcribe,
    onPreview,
    onStatement,
    onError,
    onEnded,
}) {
    const [isListening, setIsListening] = useState(false)
    const [isSpeaking, setIsSpeaking] = useState(false)
    const [pendingSegments, setPendingSegments] = useState(0)
    const [sourceKind, setSourceKind] = useState(null)
    const [includesMic, setIncludesMic] = useState(false)
    const [hasSpeakerReference, setHasSpeakerReference] = useState(false)

    const callbacksRef = useRef({})
    callbacksRef.current = {
        transcribe,
        onPreview,
        onStatement,
        onError,
        onEnded,
    }

    const listeningRef = useRef(false)
    const micStreamRef = useRef(null)
    const displayStreamRef = useRef(null)
    const recordingStreamRef = useRef(null)
    const displayEndedRef = useRef(null)
    const audioContextRef = useRef(null)
    const analyserRef = useRef(null)
    const micAnalyserRef = useRef(null)
    const recorderRef = useRef(null)
    const pollRef = useRef(null)
    const mimeTypeRef = useRef('')
    const segmentRef = useRef(null)
    const segmentingRef = useRef(SEGMENTING.mic)
    const sessionRef = useRef({ source: VOICE_SOURCES.MIC, mode: 'plain' })
    const referenceRef = useRef(freshReference())
    const queueRef = useRef(Promise.resolve())
    const segmentIdRef = useRef(0)
    // Preview clip of the current call segment, and its own (ordered) queue.
    const previewChunkRef = useRef(null)
    const previewQueueRef = useRef(Promise.resolve())

    // Preview text of segments whose final transcript is still pending,
    // as { id, text } so late preview clips can still find their segment.
    const awaitingFinalRef = useRef([])
    const currentPreviewRef = useRef('')

    const emitPreview = useCallback(() => {
        callbacksRef.current.onPreview?.(
            joinWords([
                ...awaitingFinalRef.current.map(entry => entry.text),
                currentPreviewRef.current,
            ])
        )
    }, [])

    const transcribeSegment = useCallback(async (blob, filename) => {
        const { mode, source } = sessionRef.current
        const reference = referenceRef.current
        try {
            return await callbacksRef.current.transcribe(blob, filename, {
                mode,
                source,
                speakerReference: reference.blob,
            })
        } catch (err) {
            // A clip the service won't take must not fail every segment for
            // the rest of the session: drop it and transcribe without it.
            if (!err?.referenceRejected || !reference.blob) throw err
            console.warn('[voice] speaker reference rejected, dropping it')
            reference.blob = null
            setHasSpeakerReference(false)
            return callbacksRef.current.transcribe(blob, filename, {
                mode,
                source,
            })
        }
    }, [])

    const enqueueSegment = useCallback(
        (blob, filename, previewEntry) => {
            setPendingSegments(n => n + 1)
            queueRef.current = queueRef.current
                .then(async () => {
                    let result
                    try {
                        result = normaliseResult(
                            await transcribeSegment(blob, filename)
                        )
                    } finally {
                        // The preview only ever shows what is still on its
                        // way: the final text (or the failure) takes over.
                        const index = awaitingFinalRef.current.indexOf(
                            previewEntry
                        )
                        if (index !== -1) {
                            awaitingFinalRef.current.splice(index, 1)
                        }
                        emitPreview()
                    }
                    if (result.text || result.statements.length > 0) {
                        await callbacksRef.current.onStatement?.(result)
                    }
                })
                .catch(err => callbacksRef.current.onError?.(err))
                .finally(() => setPendingSegments(n => n - 1))
        },
        [emitPreview, transcribeSegment]
    )

    // Preview clips are transcribed independently of the final segments;
    // their text lands on whichever segment they were cut from — the one
    // still recording, or one already waiting for its final transcript.
    const enqueuePreview = useCallback(
        (blob, filename, segmentId) => {
            previewQueueRef.current = previewQueueRef.current
                .then(async () => {
                    const result = normaliseResult(
                        await callbacksRef.current.transcribe(blob, filename, {
                            mode: 'plain',
                            source: sessionRef.current.source,
                            preview: true,
                        })
                    )
                    if (!result.text) return
                    if (segmentRef.current?.id === segmentId) {
                        currentPreviewRef.current = joinWords([
                            currentPreviewRef.current,
                            result.text,
                        ])
                    } else {
                        const entry = awaitingFinalRef.current.find(
                            candidate => candidate.id === segmentId
                        )
                        if (!entry) return
                        entry.text = joinWords([entry.text, result.text])
                    }
                    emitPreview()
                })
                .catch(err => {
                    console.warn('[voice] preview failed:', err?.message)
                })
        },
        [emitPreview]
    )

    const flushPreviewChunk = useCallback(() => {
        const chunk = previewChunkRef.current
        if (chunk && chunk.recorder.state !== 'inactive') chunk.recorder.stop()
    }, [])

    const startPreviewChunk = useCallback(
        segment => {
            const stream = recordingStreamRef.current
            if (!stream || !listeningRef.current) return

            const mimeType = mimeTypeRef.current
            const recorder = new MediaRecorder(
                stream,
                mimeType ? { mimeType } : undefined
            )
            const chunk = {
                recorder,
                chunks: [],
                hasSpeech: false,
                firstSpeechAt: 0,
                lastSpeechAt: 0,
            }
            recorder.ondataavailable = event => {
                if (event.data && event.data.size > 0)
                    chunk.chunks.push(event.data)
            }
            recorder.onstop = () => {
                if (previewChunkRef.current === chunk)
                    previewChunkRef.current = null
                if (chunk.hasSpeech && chunk.chunks.length > 0) {
                    const type = recorder.mimeType || mimeType || 'audio/webm'
                    enqueuePreview(
                        new Blob(chunk.chunks, { type }),
                        `preview.${extensionFor(type)}`,
                        segment.id
                    )
                }
                // Keep previewing while this segment is still recording; a
                // new segment starts its own clip.
                if (
                    listeningRef.current &&
                    segmentRef.current === segment &&
                    recorderRef.current?.state === 'recording'
                ) {
                    startPreviewChunk(segment)
                }
            }
            previewChunkRef.current = chunk
            recorder.start()
        },
        [enqueuePreview]
    )

    const startRecorder = useCallback(() => {
        const stream = recordingStreamRef.current
        if (!stream || !listeningRef.current) return

        const mimeType = mimeTypeRef.current
        const recorder = new MediaRecorder(
            stream,
            mimeType ? { mimeType } : undefined
        )
        const segment = {
            id: ++segmentIdRef.current,
            chunks: [],
            hasSpeech: false,
            startedAt: Date.now(),
            firstSpeechAt: 0,
            lastSpeechAt: 0,
        }
        segmentRef.current = segment

        recorder.ondataavailable = event => {
            if (event.data && event.data.size > 0)
                segment.chunks.push(event.data)
        }
        // Each stop() closes a complete, self-contained file (with its own
        // container header), which is what the transcription API needs — so a
        // fresh recorder is started per segment rather than slicing one stream.
        recorder.onstop = () => {
            if (segment.hasSpeech && segment.chunks.length > 0) {
                const type = recorder.mimeType || mimeType || 'audio/webm'
                const blob = new Blob(segment.chunks, { type })

                // Hand this segment's live words over to the pending list so
                // they stay visible until OpenAI's text replaces them.
                const previewEntry = {
                    id: segment.id,
                    text: currentPreviewRef.current,
                }
                awaitingFinalRef.current.push(previewEntry)
                currentPreviewRef.current = ''
                emitPreview()

                enqueueSegment(
                    blob,
                    `segment.${extensionFor(type)}`,
                    previewEntry
                )
            }
            if (listeningRef.current) startRecorder()
        }
        recorder.onerror = event => {
            callbacksRef.current.onError?.(
                event.error || new Error('Recording failed')
            )
        }

        recorderRef.current = recorder
        recorder.start()
        startPreviewChunk(segment)
    }, [enqueueSegment, emitPreview, startPreviewChunk])

    const flushSegment = useCallback(() => {
        const recorder = recorderRef.current
        if (recorder && recorder.state !== 'inactive') recorder.stop()
        flushPreviewChunk()
    }, [flushPreviewChunk])

    // Records ~4 s of the microphone alone the first time the user speaks
    // during a call; kept only if it actually contained speech.
    const startReferenceClip = useCallback(() => {
        const micStream = micStreamRef.current
        const reference = referenceRef.current
        if (!micStream) return

        const mimeType = mimeTypeRef.current
        const recorder = new MediaRecorder(
            micStream,
            mimeType ? { mimeType } : undefined
        )
        const chunks = []
        reference.recorder = recorder
        reference.speechMs = LEVEL_POLL_MS
        reference.attempts += 1

        recorder.ondataavailable = event => {
            if (event.data && event.data.size > 0) chunks.push(event.data)
        }
        recorder.onstop = () => {
            reference.recorder = null
            if (
                listeningRef.current &&
                reference.speechMs >= REFERENCE_MIN_SPEECH_MS &&
                chunks.length > 0
            ) {
                reference.blob = new Blob(chunks, {
                    type: recorder.mimeType || mimeType || 'audio/webm',
                })
                setHasSpeakerReference(true)
            }
        }
        recorder.start()
        reference.timer = setTimeout(() => {
            if (recorder.state !== 'inactive') recorder.stop()
        }, REFERENCE_CLIP_MS)
    }, [])

    const pollLevel = useCallback(() => {
        const analyser = analyserRef.current
        const segment = segmentRef.current
        if (!analyser || !segment) return

        const now = Date.now()
        const speaking = readRms(analyser) > SPEECH_RMS_THRESHOLD
        if (speaking) {
            if (!segment.hasSpeech) segment.firstSpeechAt = now
            segment.hasSpeech = true
            segment.lastSpeechAt = now
        }
        setIsSpeaking(speaking)

        const chunk = previewChunkRef.current
        if (chunk) {
            if (speaking) {
                if (!chunk.hasSpeech) chunk.firstSpeechAt = now
                chunk.hasSpeech = true
                chunk.lastSpeechAt = now
            }
            if (chunk.hasSpeech) {
                const spokenFor = now - chunk.firstSpeechAt
                const quietFor = now - chunk.lastSpeechAt
                if (
                    (spokenFor >= PREVIEW_CHUNK.minMs &&
                        quietFor >= PREVIEW_CHUNK.breathMs) ||
                    spokenFor >= PREVIEW_CHUNK.maxMs
                ) {
                    flushPreviewChunk()
                }
            }
        }

        const micAnalyser = micAnalyserRef.current
        const reference = referenceRef.current
        if (micAnalyser && !reference.blob) {
            const micSpeaking = readRms(micAnalyser) > SPEECH_RMS_THRESHOLD
            if (reference.recorder) {
                if (micSpeaking) reference.speechMs += LEVEL_POLL_MS
            } else if (
                micSpeaking &&
                reference.attempts < REFERENCE_MAX_ATTEMPTS
            ) {
                startReferenceClip()
            }
        }

        const {
            pauseMs,
            softCapMs,
            breathMs,
            maxSegmentMs,
        } = segmentingRef.current
        if (segment.hasSpeech) {
            const spokenFor = now - segment.firstSpeechAt
            const quietFor = now - segment.lastSpeechAt
            const pauseNeeded = spokenFor >= softCapMs ? breathMs : pauseMs
            if (quietFor >= pauseNeeded || spokenFor >= maxSegmentMs) {
                flushSegment()
            }
        } else if (now - segment.startedAt >= SILENT_RECYCLE_MS) {
            flushSegment()
        }
    }, [flushSegment, flushPreviewChunk, startReferenceClip])

    const releaseStreams = useCallback(() => {
        const ended = displayEndedRef.current
        if (ended) ended.track.removeEventListener('ended', ended.handler)
        displayEndedRef.current = null

        for (const ref of [
            micStreamRef,
            displayStreamRef,
            recordingStreamRef,
        ]) {
            ref.current?.getTracks().forEach(track => track.stop())
            ref.current = null
        }
        analyserRef.current = null
        micAnalyserRef.current = null
        audioContextRef.current?.close().catch(() => {})
        audioContextRef.current = null
    }, [])

    const stop = useCallback(() => {
        if (!listeningRef.current) return
        listeningRef.current = false
        clearInterval(pollRef.current)
        pollRef.current = null

        // Stopping the recorder still delivers the in-progress segment, so
        // words spoken right before the user clicked stop are not lost. The
        // reference clip is left in place for that segment's request; the
        // next start() begins with a fresh one.
        flushSegment()
        recorderRef.current = null
        segmentRef.current = null
        previewChunkRef.current = null

        const reference = referenceRef.current
        clearTimeout(reference.timer)
        if (reference.recorder && reference.recorder.state !== 'inactive') {
            reference.recorder.stop()
        }

        currentPreviewRef.current = ''
        emitPreview()

        releaseStreams()

        setIsListening(false)
        setIsSpeaking(false)
        setSourceKind(null)
        setIncludesMic(false)
        setHasSpeakerReference(false)
    }, [flushSegment, emitPreview, releaseStreams])

    const start = useCallback(
        async ({ source = VOICE_SOURCES.MIC, includeMic = true } = {}) => {
            if (listeningRef.current) return
            const fail = message =>
                callbacksRef.current.onError?.(new Error(message))

            if (!isVoiceDictationSupported()) {
                fail('Voice input is not supported in this browser.')
                return
            }
            const callSource = isCallSource(source)
            if (callSource && !isDisplayAudioSupported()) {
                fail(
                    'Sharing tab or screen audio is not supported in this browser. Use Chrome or Edge.'
                )
                return
            }

            // The share picker must open straight from the click (transient
            // activation), so it comes before any other await.
            let displayStream = null
            if (callSource) {
                try {
                    displayStream = await navigator.mediaDevices.getDisplayMedia(
                        {
                            video: true,
                            audio: {
                                echoCancellation: false,
                                noiseSuppression: false,
                                autoGainControl: false,
                                suppressLocalAudioPlayback: false,
                            },
                            systemAudio: 'include',
                            selfBrowserSurface: 'exclude',
                            preferCurrentTab: false,
                            surfaceSwitching: 'include',
                            monitorTypeSurfaces: 'include',
                        }
                    )
                } catch (err) {
                    fail(describeDisplayError(err))
                    return
                }
                // Only the audio is wanted; a live video track would keep
                // Chrome's capture indicator busy for nothing.
                displayStream.getVideoTracks().forEach(track => track.stop())
                if (displayStream.getAudioTracks().length === 0) {
                    displayStream.getTracks().forEach(track => track.stop())
                    fail(noAudioSharedMessage(source))
                    return
                }
            }

            let micStream = null
            if (!callSource || includeMic) {
                try {
                    micStream = await navigator.mediaDevices.getUserMedia({
                        audio: {
                            echoCancellation: true,
                            noiseSuppression: true,
                        },
                    })
                } catch (err) {
                    if (!callSource) {
                        fail(describeMicError(err))
                        return
                    }
                    // The call itself is still worth transcribing.
                    fail('Microphone unavailable — transcribing the call only.')
                }
            }

            const AudioContextCtor =
                window.AudioContext || window.webkitAudioContext
            const audioContext = new AudioContextCtor()
            const analyser = audioContext.createAnalyser()
            analyser.fftSize = 2048

            let recordingStream
            if (callSource) {
                // Mix everything into one stream for the recorder and the
                // level detector; the mic alone also feeds a second analyser
                // that triggers the "me" reference clip.
                const destination = audioContext.createMediaStreamDestination()
                const displaySource = audioContext.createMediaStreamSource(
                    displayStream
                )
                displaySource.connect(destination)
                displaySource.connect(analyser)
                if (micStream) {
                    const micSource = audioContext.createMediaStreamSource(
                        micStream
                    )
                    micSource.connect(destination)
                    micSource.connect(analyser)
                    const micAnalyser = audioContext.createAnalyser()
                    micAnalyser.fftSize = 1024
                    micSource.connect(micAnalyser)
                    micAnalyserRef.current = micAnalyser
                }
                recordingStream = destination.stream
            } else {
                audioContext
                    .createMediaStreamSource(micStream)
                    .connect(analyser)
                recordingStream = micStream
            }
            // Browsers may create the context suspended until a user gesture;
            // start() runs from a click, so resuming is allowed.
            audioContext.resume().catch(() => {})

            micStreamRef.current = micStream
            displayStreamRef.current = displayStream
            recordingStreamRef.current = recordingStream
            audioContextRef.current = audioContext
            analyserRef.current = analyser
            mimeTypeRef.current = pickMimeType()
            segmentingRef.current = callSource
                ? SEGMENTING.call
                : SEGMENTING.mic
            sessionRef.current = {
                source,
                mode: callSource ? 'diarize' : 'plain',
            }
            referenceRef.current = freshReference()
            listeningRef.current = true

            if (displayStream) {
                // Chrome's "Stop sharing" bar ends the track; follow it.
                const track = displayStream.getAudioTracks()[0]
                const handler = () => {
                    stop()
                    callbacksRef.current.onEnded?.('display')
                }
                track.addEventListener('ended', handler)
                displayEndedRef.current = { track, handler }
            }

            startRecorder()
            pollRef.current = setInterval(pollLevel, LEVEL_POLL_MS)
            setIsListening(true)
            setSourceKind(source)
            setIncludesMic(!!micStream)
        },
        [startRecorder, pollLevel, stop]
    )

    useEffect(() => stop, [stop])

    return {
        isListening,
        isSpeaking,
        isTranscribing: pendingSegments > 0,
        sourceKind,
        includesMic,
        hasSpeakerReference,
        start,
        stop,
    }
}
